import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Paper,
  Tooltip,
  Skeleton,
} from '@mui/material';
import { Permission } from '../types';
import {
  groupPermissionsByResource,
  RESOURCE_LABELS,
  ACTION_LABELS,
} from '../hooks/useRoles';

interface PermissionMatrixProps {
  permissions: Permission[];
  selectedPermissionIds: string[];
  onChange: (permissionIds: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const ACTIONS_ORDER = ['read', 'create', 'update', 'delete'];

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  permissions,
  selectedPermissionIds,
  onChange,
  isLoading = false,
  disabled = false,
}) => {
  const groupedPermissions = groupPermissionsByResource(permissions);
  const resources = Object.keys(groupedPermissions).sort();

  const handleTogglePermission = (permissionId: string) => {
    if (disabled) return;

    const newSelectedIds = selectedPermissionIds.includes(permissionId)
      ? selectedPermissionIds.filter((id) => id !== permissionId)
      : [...selectedPermissionIds, permissionId];

    onChange(newSelectedIds);
  };

  const handleToggleResourceRow = (resource: string) => {
    if (disabled) return;

    const resourcePermissions = groupedPermissions[resource] || [];
    const resourcePermissionIds = resourcePermissions.map((p) => p.id);
    const allSelected = resourcePermissionIds.every((id) =>
      selectedPermissionIds.includes(id)
    );

    let newSelectedIds: string[];
    if (allSelected) {
      // Deselect all permissions for this resource
      newSelectedIds = selectedPermissionIds.filter(
        (id) => !resourcePermissionIds.includes(id)
      );
    } else {
      // Select all permissions for this resource
      newSelectedIds = [
        ...selectedPermissionIds.filter(
          (id) => !resourcePermissionIds.includes(id)
        ),
        ...resourcePermissionIds,
      ];
    }

    onChange(newSelectedIds);
  };

  const handleToggleActionColumn = (action: string) => {
    if (disabled) return;

    const actionPermissions = permissions.filter((p) => p.action === action);
    const actionPermissionIds = actionPermissions.map((p) => p.id);
    const allSelected = actionPermissionIds.every((id) =>
      selectedPermissionIds.includes(id)
    );

    let newSelectedIds: string[];
    if (allSelected) {
      // Deselect all permissions for this action
      newSelectedIds = selectedPermissionIds.filter(
        (id) => !actionPermissionIds.includes(id)
      );
    } else {
      // Select all permissions for this action
      newSelectedIds = [
        ...selectedPermissionIds.filter(
          (id) => !actionPermissionIds.includes(id)
        ),
        ...actionPermissionIds,
      ];
    }

    onChange(newSelectedIds);
  };

  const handleSelectAll = () => {
    if (disabled) return;

    const allIds = permissions.map((p) => p.id);
    const allSelected = allIds.every((id) => selectedPermissionIds.includes(id));

    if (allSelected) {
      onChange([]);
    } else {
      onChange(allIds);
    }
  };

  const getPermissionForResourceAction = (resource: string, action: string) => {
    return groupedPermissions[resource]?.find((p) => p.action === action);
  };

  const isResourceFullySelected = (resource: string) => {
    const resourcePermissions = groupedPermissions[resource] || [];
    return resourcePermissions.every((p) => selectedPermissionIds.includes(p.id));
  };

  const isResourcePartiallySelected = (resource: string) => {
    const resourcePermissions = groupedPermissions[resource] || [];
    const selectedCount = resourcePermissions.filter((p) =>
      selectedPermissionIds.includes(p.id)
    ).length;
    return selectedCount > 0 && selectedCount < resourcePermissions.length;
  };

  const isActionFullySelected = (action: string) => {
    const actionPermissions = permissions.filter((p) => p.action === action);
    return actionPermissions.every((p) => selectedPermissionIds.includes(p.id));
  };

  const isActionPartiallySelected = (action: string) => {
    const actionPermissions = permissions.filter((p) => p.action === action);
    const selectedCount = actionPermissions.filter((p) =>
      selectedPermissionIds.includes(p.id)
    ).length;
    return selectedCount > 0 && selectedCount < actionPermissions.length;
  };

  const isAllSelected = () => {
    return permissions.every((p) => selectedPermissionIds.includes(p.id));
  };

  const isPartiallySelected = () => {
    const selectedCount = permissions.filter((p) =>
      selectedPermissionIds.includes(p.id)
    ).length;
    return selectedCount > 0 && selectedCount < permissions.length;
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: 200 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Select/Deselect All">
                  <Checkbox
                    checked={isAllSelected()}
                    indeterminate={isPartiallySelected()}
                    onChange={handleSelectAll}
                    disabled={disabled}
                    size="small"
                  />
                </Tooltip>
                <Typography variant="subtitle2">Resource</Typography>
              </Box>
            </TableCell>
            {ACTIONS_ORDER.map((action) => (
              <TableCell key={action} align="center" sx={{ fontWeight: 'bold' }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Tooltip title={`Select/Deselect all ${ACTION_LABELS[action as keyof typeof ACTION_LABELS]}`}>
                    <Checkbox
                      checked={isActionFullySelected(action)}
                      indeterminate={isActionPartiallySelected(action)}
                      onChange={() => handleToggleActionColumn(action)}
                      disabled={disabled}
                      size="small"
                    />
                  </Tooltip>
                  <Typography variant="caption">
                    {ACTION_LABELS[action as keyof typeof ACTION_LABELS]}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {resources.map((resource) => (
            <TableRow key={resource} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip title={`Select/Deselect all ${RESOURCE_LABELS[resource as keyof typeof RESOURCE_LABELS] || resource}`}>
                    <Checkbox
                      checked={isResourceFullySelected(resource)}
                      indeterminate={isResourcePartiallySelected(resource)}
                      onChange={() => handleToggleResourceRow(resource)}
                      disabled={disabled}
                      size="small"
                    />
                  </Tooltip>
                  <Typography variant="body2">
                    {RESOURCE_LABELS[resource as keyof typeof RESOURCE_LABELS] || resource}
                  </Typography>
                </Box>
              </TableCell>
              {ACTIONS_ORDER.map((action) => {
                const permission = getPermissionForResourceAction(resource, action);
                if (!permission) {
                  return (
                    <TableCell key={action} align="center">
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={action} align="center">
                    <Checkbox
                      checked={selectedPermissionIds.includes(permission.id)}
                      onChange={() => handleTogglePermission(permission.id)}
                      disabled={disabled}
                      size="small"
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PermissionMatrix;
